import React, { useState } from "react";
import { LocateFixed, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { locate, nearestCountry } from "@/lib/geo";
import { trackEvent } from "@/lib/analytics";
import { storage } from "@/lib/storage";
import { useNavigate, useLocation } from "react-router-dom";

/** "Use my location" — asks for GPS only on tap, matches the nearest Arab country locally and calls onPick(code). */
export default function LocateButton({ countries, onPick, className = "", foundKey = "loc_found" }) {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inKit = pathname.startsWith("/strumenti") || pathname.startsWith("/kit");
  const [busy, setBusy] = useState(false);
  const [far, setFar] = useState(null);
  const name = (c) => c?.short_names?.[lang] || c?.code;
  const arrived = (c) => {
    const last = storage.get("atlas.loc.last");
    storage.set("atlas.loc.last", c.code);
    if (inKit || last === c.code) return;
    trackEvent("arrival_notice", { code: c.code });
    toast(t("arrival_title").replace("{country}", name(c)), { description: t("arrival_desc"), duration: 8000, action: { label: t("arrival_cta"), onClick: () => navigate(`/strumenti?c=${c.code}`) } });
  };
  const run = async () => {
    setBusy(true); setFar(null); trackEvent("locate_tap");
    try {
      const pos = await locate();
      const hit = nearestCountry(countries, pos);
      if (!hit) throw new Error("unavailable");
      if (hit.inside) { onPick(hit.country.code); toast.success(t(foundKey).replace("{country}", name(hit.country))); arrived(hit.country); }
      else setFar(hit);
    } catch (e) { toast.error(t(e.message === "denied" ? "loc_denied" : "loc_unavailable")); }
    finally { setBusy(false); }
  };
  return (
    <div className={className} data-testid="locate-block">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={run} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-ochre/60 text-ochre px-3 py-1.5 text-sm font-semibold hover:bg-ochre/10 disabled:opacity-60" data-testid="locate-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />} {t("loc_btn")}
        </button>
        <span className="text-[11px] text-muted-foreground">{t("loc_hint")}</span>
      </div>
      {far && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-2xl border border-stoneLine bg-card px-3 py-2 text-sm" data-testid="locate-far">
          <img src={far.country.flag_url} alt="" className="w-6 h-4 object-cover rounded-sm" />
          <span className="text-ink flex-1">{t("loc_far").replace("{km}", far.km.toLocaleString(lang)).replace("{country}", name(far.country))}</span>
          <button type="button" onClick={() => { onPick(far.country.code); setFar(null); arrived(far.country); }} className="inline-flex items-center gap-1 rounded-full bg-ink text-sand px-3 py-1 text-xs font-semibold" data-testid="locate-use"><Check className="w-3 h-3" /> {t("loc_use")}</button>
          <button type="button" onClick={() => setFar(null)} className="p-1 text-muted-foreground" aria-label={t("close")} data-testid="locate-dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}
